<template>
  <StatsWidget
    :title="$t('stats_dashboard.health.title')"
    :description="$t('stats_dashboard.health.description')"
    icon="check-circle"
    tone="green"
    :loading="widget.loading"
    :error="widget.error"
    :has-data="!!widget.data"
    :span-full="true"
    @refresh="$emit('refresh')"
  >
    <div class="stats-health">
      <article
        v-for="metric in metrics"
        :key="metric.key"
        class="stats-health__card"
        :class="{ 'stats-health__card--accent': metric.accent }"
      >
        <header class="stats-health__head">
          <span class="stats-health__label">{{ metric.label }}</span>
          <span class="stats-health__hint">{{ metric.hint }}</span>
        </header>
        <div class="stats-health__value">{{ metric.value }}</div>
      </article>
    </div>
  </StatsWidget>
</template>

<script>
  /**
   * Health metrics (§8 + 2026-05-12 delta).
   *
   * Nine tiles in lifecycle-grouped order:
   *   • Days metrics (DSO, sales cycle, backlog days, sold backlog days)
   *   • Value metrics (AR, backlog value, sold backlog value)
   *   • Count + context (sold backlog count, annual revenue)
   *
   * Sold-backlog metrics get a subtle accent border so they stand out
   * as the new lifecycle step, without being so loud they distract
   * from the older established KPIs.
   */
  import { computed, getCurrentInstance } from 'vue';
  import StatsWidget from './StatsWidget.vue';
  import { formatCurrency, formatDays, formatInteger } from '@/utils/statsFormat';

  export default {
    name: 'StatsHealth',

    components: { StatsWidget },

    props: {
      widget: { type: Object, required: true },
    },

    emits: ['refresh'],

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const data = computed(() => props.widget.data || {});

      const metrics = computed(() => [
        // ---- Days-style metrics first ----
        {
          key: 'dso',
          label: $t('stats_dashboard.health.metrics.dso'),
          hint: $t('stats_dashboard.health.metrics.dso_hint'),
          value: formatDays(data.value.dso_days),
        },
        {
          key: 'sales_cycle',
          label: $t('stats_dashboard.health.metrics.sales_cycle'),
          hint: $t('stats_dashboard.health.metrics.sales_cycle_hint'),
          value: formatDays(data.value.avg_sales_cycle_days),
        },
        {
          key: 'backlog',
          label: $t('stats_dashboard.health.metrics.backlog'),
          hint: $t('stats_dashboard.health.metrics.backlog_hint'),
          value: formatDays(data.value.backlog_days),
        },
        {
          key: 'sold_backlog_days',
          label: $t('stats_dashboard.health.metrics.sold_backlog_days'),
          hint: $t('stats_dashboard.health.metrics.sold_backlog_days_hint'),
          value: formatDays(data.value.sold_backlog_days),
          accent: true,
        },
        // ---- Value metrics ----
        {
          key: 'accounts_receivable',
          label: $t('stats_dashboard.health.metrics.accounts_receivable'),
          hint: $t('stats_dashboard.health.metrics.accounts_receivable_hint'),
          value: formatCurrency(data.value.accounts_receivable),
        },
        {
          key: 'backlog_value',
          label: $t('stats_dashboard.health.metrics.backlog_value'),
          hint: $t('stats_dashboard.health.metrics.backlog_value_hint'),
          value: formatCurrency(data.value.backlog_value),
        },
        {
          key: 'sold_backlog_value',
          label: $t('stats_dashboard.health.metrics.sold_backlog_value'),
          hint: $t('stats_dashboard.health.metrics.sold_backlog_value_hint'),
          value: formatCurrency(data.value.sold_backlog_value),
          accent: true,
        },
        // ---- Counts + context ----
        {
          key: 'sold_backlog_count',
          label: $t('stats_dashboard.health.metrics.sold_backlog_count'),
          hint: $t('stats_dashboard.health.metrics.sold_backlog_count_hint'),
          value: formatInteger(data.value.sold_backlog_count),
          accent: true,
        },
        {
          key: 'annual_revenue',
          label: $t('stats_dashboard.health.metrics.annual_revenue'),
          hint: $t('stats_dashboard.health.metrics.annual_revenue_hint'),
          value: formatCurrency(data.value.annual_revenue),
        },
      ]);

      return { metrics };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .stats-health {
    // `auto-fit` keeps the row tight at every width — 9 cards fit on
    // ~1280px+, then wrap cleanly to 4 / 3 / 2 / 1 as space shrinks.
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 0.625rem;

    &__card {
      padding: 0.75rem 0.875rem;
      background: var(--slate-50);
      border: 1px solid var(--slate-100);
      border-radius: 0.5rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      min-width: 0;
      transition: border-color 0.15s ease;

      @include hover {
        border-color: var(--slate-200);
      }

      // Accent the sold-backlog cards so they stand out as the new
      // lifecycle step. Subtle blue tint matches the sold series in
      // the revenue chart for a coherent visual story.
      &--accent {
        background: linear-gradient(180deg, #eff6ff, #f8fafc);
        border-color: #bfdbfe;

        @include hover {
          border-color: #93c5fd;
        }
      }
    }

    &__head {
      display: flex;
      flex-direction: column;
      gap: 0.0625rem;
      min-width: 0;
    }

    &__label {
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--slate-700);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      @include ellipsis(1);
    }

    &__hint {
      font-size: 0.6875rem;
      color: var(--slate-500);
      @include ellipsis(1);
    }

    &__value {
      font-size: 1.25rem;
      font-weight: 700;
      color: var(--slate-900);
      font-variant-numeric: tabular-nums;
      line-height: 1.1;
      @include ellipsis(1);
    }
  }
</style>
